import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    MdSearch,
    MdWarning,
    MdLocalMall,
    MdSpa,
    MdBrush,
    MdColorLens,
    MdFaceRetouchingNatural,
    MdContentCut,
    MdCardGiftcard,
    MdOutlineSanitizer,
    MdKeyboardArrowLeft,
    MdKeyboardArrowRight,
    MdShoppingBasket
} from 'react-icons/md';

import { productsAPI } from 'services/api';
import { useAuth } from 'context/AuthContext';

// --- YARDIMCI FONKSİYONLAR ---
const formatCurrency = (value) => {
    return (Number(value) || 0).toLocaleString('tr-TR', {
        style: 'currency',
        currency: 'TRY',
    });
};

// Gelişmiş Türkçe Karakter Normalizasyonu ("sampuan" == "ŞAMPUAN" eşleşmesi için)
const normalizeString = (str) => {
    if (!str) return '';
    return str.toLocaleLowerCase('tr-TR')
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ş/g, 's')
        .replace(/ı/g, 'i')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c');
};

// --- GÖRSEL METERYALLER (İkonlar ve Premium Gradient Renkler) ---
const productIcons = [
    MdSpa, MdBrush, MdColorLens, MdFaceRetouchingNatural,
    MdContentCut, MdOutlineSanitizer, MdCardGiftcard, MdLocalMall
];

// HorizonUI'ın Lüks Renk Paletine Uygun Çeşitli Gradientler
const cardGradients = [
    "from-brand-400 to-brand-600", // Tema Rengi (Mavi/Mor)
    "from-blue-400 to-blue-600",   // Mavi
    "from-purple-400 to-purple-600",// Mor
    "from-pink-400 to-pink-600",   // Pembe
    "from-orange-400 to-orange-600",// Turuncu
    "from-teal-400 to-teal-600",   // Turkuaz/Teal
    "from-indigo-400 to-indigo-600",// Çivit Mavisi
    "from-rose-400 to-rose-600"    // Gül Kurusu
];

// --- SKELETON LOADER ---
const ProductsSkeleton = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 animate-pulse">
        {[...Array(8)].map((_, i) => (
            <div key={i} className="h-64 w-full rounded-[20px] bg-gray-200 dark:bg-navy-700" />
        ))}
    </div>
);

const CustomerProductList = () => {
    // --- STATELER (WORK-LOGIC BİREBİR) ---
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Sayfalama (Pagination) State
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 12; // Ekranda 12 ürün gösterimi ızgara için ideal

    const { logout } = useAuth();
    const handleAuthError = useCallback(() => {
        logout();
    }, [logout]);

    // --- API FETCH ---
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                setLoading(true);
                setError(null);
                const res = await productsAPI.getAll();
                if (res.success) {
                    // Sadece perakende satışa uygun (retail) ürünleri gösterebiliriz ama
                    // work-logic'i bozmamak adına CoreUI'da nasılsa tümünü alıyoruz.
                    setProducts(res.products || []);
                } else {
                    throw new Error('Ürünler yüklenemedi.');
                }
            } catch (err) {
                console.error('Müşteri ürün yükleme hatası:', err);
                if (String(err.message).includes('401') || String(err.message).includes('Unauthorized')) {
                    handleAuthError();
                    return;
                }
                setError(err.message || 'Ürünler yüklenirken bir hata oluştu.');
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, [handleAuthError]);

    // --- ARAMA & FİLTRELEME (Gelişmiş Türkçe Desteği ile) ---
    const filteredProducts = useMemo(() => {
        if (!searchTerm) return products;

        const lowerSearch = normalizeString(searchTerm);
        return products.filter((p) => {
            const pName = normalizeString(p.name);
            const pSku = normalizeString(p.sku);
            return pName.includes(lowerSearch) || pSku.includes(lowerSearch);
        });
    }, [products, searchTerm]);

    // Arama değişince sayfayı başa al
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // --- SAYFALAMA HESAPLARI ---
    const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
    const currentProducts = filteredProducts.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    // --- RENDER ---
    return (
        <div className="mt-5 flex w-full flex-col gap-6 pb-20 animate-fadeIn">

            {/* 1. ÜST BAŞLIK VE ARAMA ALANI */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-white p-6 rounded-[20px] shadow-3xl shadow-shadow-500 dark:bg-navy-800 dark:shadow-none border border-gray-100 dark:border-white/10">

                <div className="flex items-center gap-4 w-full md:w-auto">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-green-400 to-green-600 text-white shadow-lg shadow-green-500/30 flex-none">
                        <MdShoppingBasket className="text-3xl" />
                    </div>
                    <div>
                        <h4 className="text-xl font-bold text-navy-700 dark:text-white">Mağazamızdaki Ürünler</h4>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Salonumuzda kullanılan ve satışı yapılan premium ürünleri inceleyin.
                        </p>
                    </div>
                </div>

                {/* Arama Çubuğu */}
                <div className="relative w-full md:w-80">
                    <MdSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                    <input
                        type="text"
                        placeholder="Ürün adı veya kodu ara..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full h-12 pl-11 pr-4 rounded-xl border border-gray-200 bg-gray-50 text-sm font-bold text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white transition-all"
                    />
                </div>
            </div>

            {error && (
                <div className="p-4 rounded-xl bg-red-50 text-red-600 border border-red-200 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400 text-sm font-bold flex items-center gap-2">
                    <MdWarning className="text-xl" /> {error}
                </div>
            )}

            {/* 2. ÜRÜN KARTLARI LİSTESİ */}
            <div>
                {loading ? (
                    <ProductsSkeleton />
                ) : filteredProducts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10">
                        <MdSearch className="text-6xl text-gray-300 dark:text-gray-600 mb-4" />
                        <h5 className="text-xl font-bold text-navy-700 dark:text-white">Ürün Bulunamadı</h5>
                        <p className="text-sm font-medium text-gray-500 mt-2">Aradığınız kriterlere uygun ürün bulunmamaktadır.</p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                            {currentProducts.map((product, index) => {
                                // Her ürün için dinamik ancak tutarlı ikon ve renk ataması (ID'ye göre mod alarak her zaman aynı rengi koruruz)
                                const idNum = parseInt(product.id) || index;
                                const ProductIcon = productIcons[idNum % productIcons.length];
                                const bgGradient = cardGradients[idNum % cardGradients.length];

                                return (
                                    <div
                                        key={product.id}
                                        className={`relative flex flex-col items-center justify-between p-6 rounded-[20px] bg-gradient-to-br ${bgGradient} shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 overflow-hidden text-white min-h-[250px]`}
                                    >
                                        {/* Arka Plan Dekorasyonu */}
                                        <div className="absolute -top-10 -right-10 opacity-10">
                                            <ProductIcon className="text-[150px]" />
                                        </div>

                                        <div className="relative z-10 flex flex-col items-center w-full h-full">
                                            {/* İkon */}
                                            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/20 backdrop-blur-md border border-white/20 shadow-inner mb-4">
                                                <ProductIcon className="text-4xl" />
                                            </div>

                                            {/* Ürün Bilgisi */}
                                            <div className="flex-1 flex flex-col justify-center w-full text-center">
                                                <h5 className="text-lg font-black leading-tight line-clamp-2 drop-shadow-md mb-1">
                                                    {product.name}
                                                </h5>
                                                <p className="text-xs font-bold text-white/70 uppercase tracking-widest">
                                                    {product.sku || 'STANDART'}
                                                </p>
                                            </div>

                                            {/* Fiyat Rozeti */}
                                            <div className="mt-4 px-6 py-2 bg-white text-navy-700 dark:bg-navy-900 dark:text-white rounded-xl shadow-md">
                                                <span className="text-lg font-black">{formatCurrency(product.selling_price)}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* SAYFALAMA (PAGINATION) */}
                        {totalPages > 1 && (
                            <div className="flex flex-col sm:flex-row items-center justify-between mt-8 pt-6 border-t border-gray-100 dark:border-white/10 gap-4">
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                    <span className="font-bold text-navy-700 dark:text-white">{filteredProducts.length}</span> üründen <span className="font-bold text-navy-700 dark:text-white">{(currentPage-1)*PAGE_SIZE + 1}-{Math.min(currentPage*PAGE_SIZE, filteredProducts.length)}</span> gösteriliyor.
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all active:scale-95"
                                    >
                                        <MdKeyboardArrowLeft className="text-2xl" />
                                    </button>
                                    <div className="flex h-10 min-w-[40px] items-center justify-center rounded-xl bg-brand-500 text-white text-sm font-bold shadow-md px-3">
                                        {currentPage}
                                    </div>
                                    <button
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all active:scale-95"
                                    >
                                        <MdKeyboardArrowRight className="text-2xl" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

        </div>
    );
};

export default CustomerProductList;